import { ShieldAlert, Clock, AlertOctagon } from "lucide-react";

/**
 * Return / Refund Policy — verbatim from underwriter-approved language.
 * The four sections below are the exact policy required for high-risk RUO
 * peptide underwriting: strict returns, tight claim window, non-qualifying
 * scenarios, and a stated processing SLA.
 */
export default function RefundPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16" data-testid="refund-policy-page">
      <div className="eyebrow">Legal</div>
      <h1 className="h-display text-4xl md:text-6xl mt-2 leading-[1.04]">Return &amp; Refund Policy.</h1>
      <p className="mt-4 text-[13px] font-mono uppercase tracking-[0.18em] text-[var(--ink-muted)]">
        Effective February 1, 2026
      </p>

      <div className="mt-6 border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-4 text-[var(--ink-soft)] text-sm leading-relaxed">
        Our products are sensitive research compounds handled under strict chain-of-custody rules.
        Please read the four sections below — every scenario a buyer can encounter is covered.
      </div>

      <div className="mt-12 space-y-10 text-[var(--ink-soft)]">
        <Section title="Returns" icon={<ShieldAlert className="h-5 w-5 text-[var(--terra)]" />} testid="section-returns">
          <p>Because our products are sensitive research compounds, we cannot accept returns once
          a product has left our facility, except where the product is defective, damaged in
          transit, or shipped in error.</p>
        </Section>

        <Section title="Claims window" icon={<Clock className="h-5 w-5 text-[var(--terra)]" />} testid="section-claims">
          <p>Report any damage, error, or quality concern within <strong>7 days</strong> of
          delivery to <a className="underline underline-offset-2 hover:text-[var(--terra)]" href="mailto:support@nebulaaminos.com">support@nebulaaminos.com</a> with
          your order number and the batch/lot number. We will review against the product's COA
          and, where warranted, issue a replacement or refund to the original payment method.</p>
        </Section>

        <Section title="Non-qualifying" icon={<AlertOctagon className="h-5 w-5 text-[var(--crimson)]" />} testid="section-nonqualifying">
          <p>Buyer's remorse, incorrect product selection, and refused/undeliverable shipments due
          to an incorrect address provided by the buyer are not eligible for refund.</p>
        </Section>

        <Section title="Processing" icon={<Clock className="h-5 w-5 text-[var(--terra)]" />} testid="section-processing">
          <p>Approved refunds are issued within <strong>5–10 business days</strong>.</p>
        </Section>

        <Section title="Contact" testid="section-contact">
          <p>Nebula Aminos LLC · 113 W Ryker Ln, Midvale UT 84047, USA · 305-877-7739 ·{" "}
            <a className="underline underline-offset-2 hover:text-[var(--terra)]" href="mailto:support@nebulaaminos.com">support@nebulaaminos.com</a></p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, icon, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2.5">
        {icon}
        <h2 className="font-display text-2xl font-medium text-[var(--ink)]">{title}</h2>
      </div>
      <div className="mt-3 leading-relaxed text-[var(--ink-soft)] space-y-3">{children}</div>
    </section>
  );
}
